import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import { EmissionPointPicker } from '../../../components/bookkeeping/EmissionPointPicker.js';
import { SriStatusTrack } from '../../../components/bookkeeping/SriStatusTrack.js';
import type { Account, AccountNode, Invoice, TaxRate } from '../../../types/bookkeeping.js';

const TAX_DIVISOR = 100_000_000n;

function flattenTree(nodes: AccountNode[]): Account[] {
  const out: Account[] = [];
  const walk = (ns: AccountNode[]) => {
    for (const n of ns) {
      out.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

interface MotivoForm {
  razon: string;
  valor: string; // cents
  tax_rate_id: string;
  account_id: string;
}

function defaultMotivo(): MotivoForm {
  return { razon: '', valor: '0', tax_rate_id: '', account_id: '' };
}

interface NDDetail {
  id: string;
  number: string;
  source_invoice_id: string;
  issue_date: string;
  currency: string;
  total_sin_impuestos: string;
  total_iva: string;
  grand_total: string;
  sri_status: string | null;
  lines: Array<{ razon: string; valor: string; tax_amount: string }>;
}

const fmtMoney = (cents: string): string => {
  const n = BigInt(cents || '0');
  return `${n / 100n}.${String(n % 100n).padStart(2, '0')}`;
};

export function NotaDebitoPage() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: ndId } = useParams<{ id: string }>();

  const [sourceInvoiceId, setSourceInvoiceId] = useState('');
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [emissionPointId, setEmissionPointId] = useState<string | null>(null);
  const [motivos, setMotivos] = useState<MotivoForm[]>([defaultMotivo()]);
  const [error, setError] = useState<string | null>(null);

  // ── Detail mode ──────────────────────────────────────────────
  const detailQ = useQuery({
    queryKey: ['bk-nota-debito', ndId],
    queryFn: () => api<NDDetail>(`/bookkeeping/sales/nota-debito/${ndId}`),
    enabled: !!activeOrgId && !!ndId,
  });

  // ── Create-mode reference data ───────────────────────────────
  const { data: invoicesData } = useQuery({
    queryKey: ['bk-invoices-all', activeOrgId],
    queryFn: () => api<{ invoices: Invoice[] }>('/bookkeeping/sales/invoices?limit=100'),
    enabled: !!activeOrgId && !ndId,
  });
  const { data: taxRatesData } = useQuery({
    queryKey: ['bookkeeping-tax-rates', activeOrgId],
    queryFn: () => api<{ tax_rates: TaxRate[] }>('/bookkeeping/sales/tax-rates?limit=100'),
    enabled: !!activeOrgId && !ndId,
  });
  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId && !ndId,
  });
  const taxRates = taxRatesData?.tax_rates ?? [];
  const accounts = flattenTree(accountsData?.tree ?? []);

  const totals = motivos.reduce(
    (acc, m) => {
      const valor = BigInt(m.valor || '0');
      const tr = taxRates.find((r) => r.id === m.tax_rate_id);
      const pct = tr ? BigInt(tr.percentage) : 0n;
      const tax = (valor * pct) / TAX_DIVISOR;
      acc.subtotal += valor;
      acc.iva += tax;
      return acc;
    },
    { subtotal: 0n, iva: 0n },
  );
  const grandTotal = totals.subtotal + totals.iva;

  const createMut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        source_invoice_id: sourceInvoiceId,
        issue_date: issueDate,
        currency: 'USD',
        emission_point_id: emissionPointId ?? undefined,
        lines: motivos.map((m) => ({
          razon: m.razon,
          valor: m.valor,
          tax_rate_id: m.tax_rate_id,
          account_id: m.account_id,
        })),
      });
      return api<{ id: string }>('/bookkeeping/sales/nota-debito', { method: 'POST', body });
    },
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['bk-notas-debito'] });
      nav(`/app/bookkeeping/sales/nota-debito/${created.id}`);
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(err.body?.error ?? err.message ?? 'Error');
    },
  });

  function submit() {
    if (!sourceInvoiceId) return setError('source_required');
    if (motivos.length === 0 || motivos.some((m) => !m.razon || !m.tax_rate_id || !m.account_id))
      return setError('no_lines');
    setError(null);
    createMut.mutate();
  }

  // ── Render: detail mode ──────────────────────────────────────
  if (ndId) {
    const nd = detailQ.data;
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold">
          {t('notaDebito.title')} {nd ? nd.number : ''}
        </h1>
        {nd && (
          <>
            <div className="rounded border bg-white p-4 text-sm">
              <div>
                <span className="font-semibold">{t('notaDebito.total')}:</span>{' '}
                {fmtMoney(nd.grand_total)} {nd.currency}
              </div>
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="px-2 py-1">{t('notaDebito.razon')}</th>
                    <th className="px-2 py-1">{t('notaDebito.valor')}</th>
                  </tr>
                </thead>
                <tbody>
                  {nd.lines.map((l, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable order
                    <tr key={i} className="border-b">
                      <td className="px-2 py-1">{l.razon}</td>
                      <td className="px-2 py-1">{fmtMoney(l.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <SriStatusTrack docType="nota-debito" docId={nd.id} />
          </>
        )}
      </div>
    );
  }

  // ── Render: create mode ──────────────────────────────────────
  const invoices = invoicesData?.invoices ?? [];
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('notaDebito.create')}</h1>

      <div className="grid grid-cols-2 gap-3 rounded border bg-white p-4 text-sm">
        <label>
          {t('notaDebito.source_invoice')}
          <select
            value={sourceInvoiceId}
            onChange={(e) => setSourceInvoiceId(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          >
            <option value="">—</option>
            {invoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.number}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('notaDebito.total')}
          <input
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          />
        </label>
        <EmissionPointPicker value={emissionPointId} onChange={setEmissionPointId} />
      </div>

      <div className="rounded border bg-white p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="px-2 py-1">{t('notaDebito.razon')}</th>
              <th className="px-2 py-1">{t('notaDebito.valor')}</th>
              <th className="px-2 py-1">{t('notaDebito.tax_rate')}</th>
              <th className="px-2 py-1">{t('notaDebito.account')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {motivos.map((m, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: stable row order
              <tr key={i} className="border-b">
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={m.razon}
                    onChange={(e) =>
                      setMotivos((ms) =>
                        ms.map((x, j) => (j === i ? { ...x, razon: e.target.value } : x)),
                      )
                    }
                    className="w-full rounded border px-2 py-1"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={m.valor}
                    onChange={(e) =>
                      setMotivos((ms) =>
                        ms.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)),
                      )
                    }
                    className="w-28 rounded border px-2 py-1 text-right font-mono"
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    value={m.tax_rate_id}
                    onChange={(e) =>
                      setMotivos((ms) =>
                        ms.map((x, j) => (j === i ? { ...x, tax_rate_id: e.target.value } : x)),
                      )
                    }
                    className="rounded border px-2 py-1"
                  >
                    <option value="">—</option>
                    {taxRates.map((tr) => (
                      <option key={tr.id} value={tr.id}>
                        {tr.code}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <select
                    value={m.account_id}
                    onChange={(e) =>
                      setMotivos((ms) =>
                        ms.map((x, j) => (j === i ? { ...x, account_id: e.target.value } : x)),
                      )
                    }
                    className="rounded border px-2 py-1"
                  >
                    <option value="">—</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <button
                    type="button"
                    onClick={() => setMotivos((ms) => ms.filter((_, j) => j !== i))}
                    className="text-red-600 hover:underline"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          onClick={() => setMotivos((ms) => [...ms, defaultMotivo()])}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          {t('notaDebito.add_line')}
        </button>
      </div>

      <div className="flex items-center justify-between rounded border bg-white p-4 text-sm">
        <div className="space-y-1">
          <div>
            {t('notaDebito.subtotal')}:{' '}
            <span className="font-mono">{fmtMoney(totals.subtotal.toString())}</span>
          </div>
          <div>
            {t('notaDebito.iva')}:{' '}
            <span className="font-mono">{fmtMoney(totals.iva.toString())}</span>
          </div>
          <div className="font-semibold">
            {t('notaDebito.total')}:{' '}
            <span className="font-mono">{fmtMoney(grandTotal.toString())}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={createMut.isPending}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
        >
          {t('notaDebito.create')}
        </button>
      </div>
      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {t(`notaDebito.errors.${error}`, { defaultValue: error })}
        </p>
      )}
    </div>
  );
}
